import React, { useEffect, useState, useMemo } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { InstallationType,LatoInstallation } from "../components/forms/Pergola_type";
import { ChiusuraChoice } from "../components/side/chiusure_choice";
import { LatoSide } from "../../../../../models/pergola_configuration";
import { useStoreSelector } from "../../../../../../hooks/StoreHooks";

type MainSide = "back" | "front" | "left" | "right";

const LATO_LABELS = {
  front: "Fronte",
  back: "Retro",
  left: "Sinistra",
  right: "Destra",
} as const;

interface SideConfig {
  main: MainSide;
  label: string;
  subCount: number;
  subPrefix: string;
}

const SideChoices: React.FC = () => {
  const [mainSide, setMainSide] = useState<MainSide>("front");
  const [subSide, setSubSide] = useState<string | null>(null);
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);

  const addossamento = useStoreSelector(
    (store) => store.pergolaConfigurationBuilder.configuration?.dimensioni.installazione
  );
  const lato = useStoreSelector(
    (store) => store.pergolaConfigurationBuilder.configuration?.dimensioni.lati
  );
  console.log("LAti",lato)
  useEffect(() => {
    if (addossamento === InstallationType.patio) {
      setMainSide("left");
    }
  }, [addossamento]);
  const larghezzaSoletta = (pergolaConfiguration.configuration?.dimensioni.larghezza ?? 2000) / 1000;
  const lunghezzaSoletta = (pergolaConfiguration.configuration?.dimensioni.profondita ?? 4000) / 1000;
  let nstacchi = Math.floor(larghezzaSoletta / 4.1);
  const tolleranza = 1e-9;

  if (Math.abs(nstacchi * 4.1 - larghezzaSoletta) > tolleranza) {
      nstacchi += 1;
  }
  let nstacchiy = Math.floor(lunghezzaSoletta / 7) ;
  if (Math.abs(nstacchiy * 7 - lunghezzaSoletta) > tolleranza) {
      nstacchiy += 1;
  }

  const sideConfigs: SideConfig[] = useMemo(() => {
    const configs: SideConfig[] = [];

    const isAddossato = (direzione: LatoSide["direzione"]) =>
      lato?.some(
        (l) => l.direzione === direzione && l.tipo === LatoInstallation.addossato
      );
    if (!isAddossato("Fronte")) {
      configs.push({ main: "front", label: "A", subCount: nstacchi, subPrefix: "A" });
    }

    if (!isAddossato("Sinistra")) {
      configs.push({ main: "left", label: "B", subCount: nstacchiy, subPrefix: "B" });
    }

    if (!isAddossato("Retro")) {
      configs.push({ main: "back", label: "C", subCount: nstacchi, subPrefix: "C" });
    }

    if (!isAddossato("Destra")) {
      configs.push({ main: "right", label: "D", subCount: nstacchiy, subPrefix: "D" });
    }

    return configs;
  }, [lato, nstacchi, nstacchiy]);

  useEffect(() => {
    const available = sideConfigs.map((c) => c.main);
    if (!available.includes(mainSide) && available.length > 0) {
      setMainSide(available[0]);
      setSubSide(null);
    }
  }, [sideConfigs, mainSide]);

  useEffect(() => {
    const config = sideConfigs.find((c) => c.main === mainSide);
    if (!config) return;

    if (config.subCount === 1) {
      setSubSide(null);
      return;
    }

    const valid = Array.from({ length: config.subCount }, (_, i) => `${config.subPrefix}${i + 1}`);
    if (!subSide || !valid.includes(subSide)) {
      setSubSide(valid[0]);
    }
  }, [mainSide, sideConfigs, subSide]);

  const handleMainChange = (_: React.SyntheticEvent, value: MainSide) => {
    setMainSide(value);
  };

  const handleSubChange = (_: React.SyntheticEvent, value: string) => {
    setSubSide(value);
  };

  const boxStyles = {
    back: {
      width: 40, height: 40, border: "1px dashed #999",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      "& > div": { width: "80%", height: 4, bgcolor: "primary.main" },
    },
    front: {
      width: 40, height: 40, border: "1px dashed #999",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      "& > div": { width: "80%", height: 4, bgcolor: "primary.main" },
    },
    left: {
      width: 40, height: 40, border: "1px dashed #999",
      display: "flex", alignItems: "center", justifyContent: "flex-start",
      "& > div": { width: 4, height: "80%", bgcolor: "primary.main" },
    },
    right: {
      width: 40, height: 40, border: "1px dashed #999",
      display: "flex", alignItems: "center", justifyContent: "flex-end",
      "& > div": { width: 4, height: "80%", bgcolor: "primary.main" },
    },
  } as const;

  const tabStyle = {
    flexDirection: "column" as const,
    textTransform: "none" as const,
    p: 1.5,
    minWidth: 80,
    border: "1px solid transparent",
    borderRadius: "8px 8px 0 0",
    bgcolor: "transparent",
    transition: "0.3s",
    "&.Mui-selected": {
      bgcolor: "background.paper",
      borderColor: "primary.main",
      borderBottom: "1px solid white",
    },
  };

  const subTabStyle = {
    minHeight: 32,
    minWidth: 40,
    p: 0.5,
    fontSize: "0.75rem",
    textTransform: "none" as const,
  };

  const iconMap: Record<MainSide, JSX.Element> = {
    front: <Box component='div' sx={boxStyles.front}><Box component='div'/></Box>,
    back: <Box component='div' sx={boxStyles.back}><Box component='div'/></Box>,
    left: <Box component='div' sx={boxStyles.left}><Box component='div'/></Box>,
    right: <Box component='div' sx={boxStyles.right}><Box component='div'/></Box>,
  };

  const currentConfig = sideConfigs.find((c) => c.main === mainSide);
  const hasSubTabs = currentConfig && currentConfig.subCount > 1;

  // === SEGMENTO: SEMPRE number ===
  const segmento: number = subSide
    ? parseInt(subSide.replace(/\D/g, ""), 10)
    : currentConfig?.subCount || 1;
  const latoLabel = mainSide ? LATO_LABELS[mainSide] : "Fronte";

  return (
    <Box component='div'>
      <Tabs
        value={mainSide}
        onChange={handleMainChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ display: "flex", gap: 2, "& .MuiTabs-indicator": { display: "none" } }}
      >
        {sideConfigs.map((config) => (
          <Tab
            key={config.main}
            value={config.main}
            sx={tabStyle}
            icon={iconMap[config.main]}
            iconPosition="top"
            label={config.label}
          />
        ))}
      </Tabs>

      <Box
        component='div'
        sx={{
          border: "1px solid",
          borderColor: "primary.main",
          borderRadius: 2,
          borderTopLeftRadius: hasSubTabs ? 0 : 2,
          borderTopRightRadius: hasSubTabs ? 0 : 2,
          p: 2,
          mt: hasSubTabs ? "-1px" : "-1px",
          bgcolor: "background.paper",
        }}
      >
        {hasSubTabs && currentConfig && (
          <Box component='div' sx={{ borderBottom: 1, borderColor: "primary.main", pl: 2, py: 1 }}>
            <Tabs value={subSide || ""} onChange={handleSubChange}>
              {Array.from({ length: currentConfig.subCount }, (_, i) => {
                const label = `${currentConfig.subPrefix}${i + 1}`;
                return (
                  <Tab key={label} value={label} label={label} sx={subTabStyle} />
                );
              })}
            </Tabs>
          </Box>
        )}
        {currentConfig && (
          <ChiusuraChoice
            lato={latoLabel}
            segmento={segmento}
          />
        )}
      </Box>
    </Box>
  );
};

export default SideChoices;